import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Lock, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getSportCategory, getSportEmoji, isFixtureLocked, getCountdown } from "@/lib/fixtures";
import { apiGetPredictions, apiGetResults, apiGetFixtures } from "@/lib/storage";
import { useSlip, UCL_LOGO, KCL_LOGO } from "@/components/Layout";

export default function SportPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { addToSlip, isItemInSlip } = useSlip();
  const decodedCat = decodeURIComponent(category);
  const [predictions, setPredictions] = useState([]);
  const [results, setResults] = useState([]);
  const [allFixtures, setAllFixtures] = useState([]);

  useEffect(() => {
    apiGetPredictions().then(setPredictions);
    apiGetResults().then(setResults);
    apiGetFixtures().then(setAllFixtures);
    const handler = () => {
      apiGetPredictions().then(setPredictions);
      apiGetResults().then(setResults);
    };
    window.addEventListener("predictions-updated", handler);
    return () => window.removeEventListener("predictions-updated", handler);
  }, []);

  const predMap = {};
  predictions.forEach(p => { predMap[p.fixture_id] = p; });
  const resultMap = {};
  results.forEach(r => { resultMap[r.fixture_id] = r; });

  const fixtures = allFixtures.filter(f => getSportCategory(f.sport) === decodedCat);
  const emoji = getSportEmoji(decodedCat);

  if (fixtures.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">No fixtures found for this sport.</p>
        <button onClick={() => navigate("/")} className="text-red-500 text-xs mt-2 underline">Go Home</button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid={`sport-page-${decodedCat}`}>
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded text-slate-400 hover:text-white" data-testid="sport-back-btn"><ArrowLeft size={18} /></button>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{emoji}</span>
            <h1 className="text-2xl font-bold text-white capitalize" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{decodedCat}</h1>
          </div>
          <p className="text-xs text-slate-500">{fixtures.length} fixture{fixtures.length !== 1 ? "s" : ""} | +10 points per correct pick</p>
        </div>
      </div>
      <div className="red-gradient-line" />
      <div className="space-y-3">
        {fixtures.map(fixture => {
          const locked = isFixtureLocked(fixture);
          const countdown = getCountdown(fixture);
          const prediction = predMap[fixture.id];
          const result = resultMap[fixture.id];
          const slipItem = isItemInSlip(fixture.id);
          const hasResult = result && result.winner;
          return (
            <div key={fixture.id} className={`bg-[#111827] border border-white/5 rounded-sm overflow-hidden ${locked && !hasResult ? "opacity-50" : ""}`} data-testid={`sport-fixture-${fixture.id}`}>
              <div className="flex items-center justify-between px-4 py-2 bg-white/[0.02] border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{emoji}</span>
                  <span className="text-xs font-semibold text-white uppercase tracking-wider">{fixture.sport}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 font-mono">{fixture.date} | {fixture.time}</span>
                  {hasResult ? (
                    <Badge className="text-[10px] bg-white/5 text-white border-white/10 font-bold">
                      {result.winner === "A" ? "UCL" : "KCL"} won
                    </Badge>
                  ) : locked ? (
                    <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400 gap-1"><Lock size={8} /> LOCKED</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 gap-1 font-mono"><Clock size={8} /> {countdown}</Badge>
                  )}
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-center gap-8">
                  <button data-testid={`sport-${fixture.id}-pick-ucl`} disabled={locked || !!prediction}
                    onClick={() => addToSlip(fixture.id, "A", fixture)}
                    className={`pick-btn flex-col py-6 px-8 rounded ${prediction?.team === "A" ? "selected" : slipItem?.team === "A" ? "selected" : ""} ${hasResult && result.winner === "A" ? "border-green-500/50 bg-green-500/10" : ""}`}>
                    <img src={UCL_LOGO} alt="UCL" className="w-14 h-14 object-contain mb-2" />
                    <span className="text-lg font-bold">UCL</span>
                    <span className="text-[10px] text-slate-500 mt-0.5">University College London</span>
                    {hasResult && result.winner === "A" && <span className="text-[10px] text-green-400 font-bold mt-1">Winner</span>}
                  </button>
                  <div className="flex flex-col items-center">
                    <span className="text-lg text-slate-600 font-bold">VS</span>
                    <span className="text-xs text-slate-600 font-mono mt-1">{fixture.time}</span>
                  </div>
                  <button data-testid={`sport-${fixture.id}-pick-kcl`} disabled={locked || !!prediction}
                    onClick={() => addToSlip(fixture.id, "B", fixture)}
                    className={`pick-btn flex-col py-6 px-8 rounded ${prediction?.team === "B" ? "selected" : slipItem?.team === "B" ? "selected" : ""} ${hasResult && result.winner === "B" ? "border-green-500/50 bg-green-500/10" : ""}`}>
                    <img src={KCL_LOGO} alt="KCL" className="w-14 h-14 object-contain mb-2" />
                    <span className="text-lg font-bold">KCL</span>
                    <span className="text-[10px] text-slate-500 mt-0.5">King's College London</span>
                    {hasResult && result.winner === "B" && <span className="text-[10px] text-green-400 font-bold mt-1">Winner</span>}
                  </button>
                </div>
                {prediction && (
                  <div className="mt-4 pt-4 border-t border-white/5 text-center">
                    {prediction.status === "won" ? (
                      <p className="text-xs text-green-400 flex items-center justify-center gap-1">
                        <CheckCircle size={12} /> Correct! You earned <span className="font-bold font-mono">+10</span> points
                      </p>
                    ) : prediction.status === "lost" ? (
                      <p className="text-xs text-red-400 flex items-center justify-center gap-1">
                        <XCircle size={12} /> Incorrect prediction. No points earned.
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400">
                        Your prediction: <span className="text-white font-bold">{prediction.team === "A" ? "UCL" : "KCL"}</span>
                        {" "}| Earns <span className="text-green-400 font-mono font-bold">+10</span> points if correct
                      </p>
                    )}
                  </div>
                )}
                {!locked && !prediction && (
                  <p className="text-center text-[10px] text-slate-600 mt-4 uppercase tracking-widest">Select a team to add to your prediction slip</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
